/* eslint-disable react/no-unescaped-entities, import/order, jsx-a11y/control-has-associated-label, react/require-default-props, prettier/prettier */

import React, { useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import I18n, { translate } from '@utils/i18n';

import BooleanSelect from '@components/Kendo/BooleanSelect';
import DateRangePicker from '@components/Kendo/DateRangePicker';

import { DEFAULT_PAGE_SIZE, pageSizes, buildGridSettings } from '@constants/Kendo/gridSettings';
import { isProduction } from '@utils/environment';
import { actionTypes, reducer, buildInitialFiltersState, isDateRangeStateField } from '../reducer';

import restoreGridPage from '../../../../helpers/restoreGridPage';
import CustomScrollbar from '../../../../components/CustomScrollbar/CustomScrollbar';
import GenericMultiSelect from '../../../../components/Multiselects/GenericMultiSelect/GenericMultiSelect.tsx';
import EditEndDateModal from '../../EditEndDateModal/index.tsx';
import { showSuccessToast } from '@ui/molecules/Toast';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import renderKendoTemplate from '@helpers/renderKendoTemplate';

// Add JQuery
const { kendo } = window;
const { $, confirm, ConfirmationModal } = window;

const DEFAULT_NAMESPACE = 'inspection_series.datatable';
const DEFAULT_CACHE_KEY = 'inspection_series/index';
const DEFAULT_NON_HIDABLE_COLUMNS = [ 'selectInspectionSeries', 'id', 'actions'];

// TODO: re-visit and see how we could minify the relationship between sprockets and webpacker part.
// Ideally, we should not mix them. But to be eliminate the waiting time for the duration
// of whole project being re-written and to be able to use React now - it's worth it.
function LegacyInspectionSeriesIndex(props) {
  const {
    namespace,
    inspectionSeriesUrl,
    buildingsForSelect,
    accountsForSelect,
    territoriesForSelect,
    cacheKey,
    defaultHiddenColumns,
    nonHidableColumns,
    hasShowExpiredSeriesFilter,
    hasShowExpirationDateFilter,
    hasShowBuildingFilter,
    hasShowAccountFilter,
    hasShowStatusFilter,
    hasShowAutoExtendFilter,
    hasDisableAllFilters,
    hasDisableAllBulkActions,
    pageSize,
    minEndDate,
    maxEndDate
  } = props;

  const [editingSeries, setEditingSeries] = useState(null);

  const kendoCacheKey = `kendo/${cacheKey}`;
  const containerRef = useRef();
  const tableRef = useRef();
  const [state, dispatch] = useReducer(reducer, {
    tableFingerprint: undefined,
    inspection_series: [],
    filters: buildInitialFiltersState()
  });

  // Mechanism to make state being up-to-date in Kendo grid callbacks.
  // Src: https://stackoverflow.com/a/60643670/20974169
  const filtersRef = useRef();
  filtersRef.current = state.filters;

  const tableFingerprintRef = useRef();
  tableFingerprintRef.current = state.tableFingerprint;

  const isMounted = !!state.tableFingerprint;
  const isMountedForCallbacks = () => !!tableFingerprintRef.current;
  let cachedPage = 1;

  useEffect(() => {
    setupTable();
    setupTableControls();
    loadCacheFromLocalStorage();

    dispatch({ type: actionTypes.TABLE_RELOADED });
  }, []);

  // Delegated because Kendo re-renders rows on every page/sort, so per-row handlers would be lost.
  useEffect(() => {
    const onEditExpirationClick = (event) => {
      const trigger = event.target.closest('.js-edit-expiration');
      if (!trigger) return;

      event.preventDefault();
      setEditingSeries({
        id: Number(trigger.dataset.seriesId),
        buildingId: Number(trigger.dataset.buildingId),
        endDate: trigger.dataset.endDate || ''
      });
    };

    document.addEventListener('click', onEditExpirationClick);
    return () => document.removeEventListener('click', onEditExpirationClick);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    reloadTable();
    reloadTableControls();
  }, [state.tableFingerprint]);

  // ===================== JS Part ================================

  const setupTable = () => {
    const gridSettings = buildGridSettings(tableRef.current, {
      columns: [
        {
          field: 'selectInspectionSeries',
          title: translate('table.headers.select_inspection_series', { namespace }),
          sortable: false,
          columnMenu: false,
          width: 50,
          headerTemplate: checkBoxHeaderTemplate,
          template: checkBoxTemplate,
          hidden: defaultHiddenColumns.includes('selectInspectionSeries'), // to hide column by default
          menu: !nonHidableColumns.includes('selectInspectionSeries'), // to show column in menu
          attributes: {
            class: 'table__cell--selectable'
          }
        },
        {
          field: 'id',
          title: translate('table.headers.id', { namespace }),
          template: idTemplate,
          width: 130,
          hidden: defaultHiddenColumns.includes('id'),
          menu: !nonHidableColumns.includes('id')
        },
        {
          field: 'referenceNumber',
          title: translate('table.headers.reference_number', { namespace }),
          template: referenceNumberTemplate,
          hidden: defaultHiddenColumns.includes('referenceNumber'),
          menu: !nonHidableColumns.includes('referenceNumber')
        },
        {
          field: 'contractNumber',
          title: translate('table.headers.contract_number', { namespace }),
          template: contractNumberTemplate,
          hidden: defaultHiddenColumns.includes('contractNumber'),
          menu: !nonHidableColumns.includes('contractNumber')
        },
        {
          field: 'building',
          title: I18n.t('generic.building'),
          template: buildingTemplate,
          hidden: defaultHiddenColumns.includes('building'),
          menu: !nonHidableColumns.includes('building')
        },
        {
          field: 'account',
          title: I18n.t('generic.account'),
          template: accountTemplate,
          hidden: defaultHiddenColumns.includes('account'),
          menu: !nonHidableColumns.includes('account')
        },
        {
          field: 'frequencies',
          title: translate('table.headers.frequencies', { namespace }),
          sortable: false,
          template: frequenciesTemplate,
          hidden: defaultHiddenColumns.includes('frequencies'),
          menu: !nonHidableColumns.includes('frequencies')
        },
        {
          field: 'expirationDate',
          title: translate('table.headers.expiration_date', { namespace }),
          template: expirationDateTemplate,
          hidden: defaultHiddenColumns.includes('expirationDate'),
          menu: !nonHidableColumns.includes('expirationDate')
        },
        {
          field: 'systemAsset',
          title: translate('table.headers.system_asset', { namespace }),
          sortable: false,
          template: systemAssetTemplate,
          hidden: defaultHiddenColumns.includes('systemAsset'),
          menu: !nonHidableColumns.includes('systemAsset')
        },
        {
          field: 'inspectionsCount',
          title: translate('table.headers.inspections_count', { namespace }),
          sortable: false,
          template: inspectionsCountTemplate,
          hidden: defaultHiddenColumns.includes('inspectionsCount'),
          menu: !nonHidableColumns.includes('inspectionsCount')
        },
        {
          field: 'status',
          title: translate('table.headers.status', { namespace }),
          template: statusTemplate,
          hidden: defaultHiddenColumns.includes('status'),
          menu: !nonHidableColumns.includes('status')
        },
        {
          field: 'autoExtend',
          title: translate('table.headers.auto_extend', { namespace }),
          template: autoExtendTemplate,
          hidden: defaultHiddenColumns.includes('autoExtend'),
          menu: !nonHidableColumns.includes('autoExtend')
        },
        {
          field: 'actions',
          title: ' ',
          sortable: false,
          columnMenu: false,
          template: actionsTemplate,
          hidden: defaultHiddenColumns.includes('actions'),
          menu: !nonHidableColumns.includes('actions'),
          attributes: {
            class: 'table__cell--options--col-actions'
          }
        }
      ],
      pageable: {
        pageSize,
        pageSizes: pageSizes(pageSize)
      },
      dataSource: {
        schema: {
          model: {
            fields: {
              id: { type: 'number' },
              referenceNumber: { type: 'string' },
              contractNumber: { type: 'string' },
              building: { type: 'string' },
              account: { type: 'string' },
              frequencies: { type: 'string' },
              expirationDate: { type: 'string' },
              systemAsset: { type: 'string' },
              inspectionsCount: { type: 'number' },
              status: { type: 'string' },
              autoExtend: { type: 'string' }
            }
          }
        },
        sort: { field: 'id', dir: 'desc' }
      },
      culture: $('body').attr('data-locale'),
      scrollable: false,
      columnShow: saveCacheToLocalStorage,
      columnHide: saveCacheToLocalStorage,
      dataBinding: () => {
        $('.rerender-inspection-series-popup').remove();
      },
      dataBound: () => {
        new ConfirmationModal().init();
        saveCacheToLocalStorage();
        // eslint-disable-next-line no-undef
        loadDropdowns({ gridSelector: '#inspection-series-grid' });
        restoreGridPage(cachedPage, $(tableRef.current).data('kendoGrid').dataSource);
      }
    });
    $(tableRef.current).kendoGrid(gridSettings);
  };

  const checkBoxTemplate = renderKendoTemplate(
    <div className="--checkbox">
      <input type="checkbox" data-inspection-series-id="#= id #" data-js-bulk-single-checkbox />
    </div>
  );

  const checkBoxHeaderTemplate = renderKendoTemplate(
    <input type="checkbox" className="--checkbox" data-js-bulk-all-checkbox />
  );

  const expirationDateTemplate = renderKendoTemplate(
    <>
      {'# if (expirationDate) { #'}
      <span>#= expirationDate #</span>
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const systemAssetTemplate = renderKendoTemplate(
    <>
      {'# if (systemAsset) { #'}
      <span>#= systemAsset #</span>
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const inspectionsCountTemplate = renderKendoTemplate(
    <>
      {'# if (inspectionsCount) { #'}
      <span>#= inspectionsCount #</span>
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const statusTemplate = renderKendoTemplate(
    <>
      {'# if (status) { #'}
      <span>#= status #</span>
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const autoExtendTemplate = renderKendoTemplate(
    <>
      {'# if (autoExtend) { #'}
      <span>#= autoExtend #</span>
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const idTemplate = renderKendoTemplate(
    <>
      {'# if (id) { #'}
      {'# if (showLink) { #'}
      <u>
        <a href="#= showLink #">#= id #</a>
      </u>
      {'# } else { #'}
      <span>#= id #</span>
      {'# } #'}
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const referenceNumberTemplate = renderKendoTemplate(
    <>
      {'# if (referenceNumber) { #'}
      <span>#= referenceNumber #</span>
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const contractNumberTemplate = renderKendoTemplate(
    <>
      {'# if (contractNumber) { #'}
      <span>#= contractNumber #</span>
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const accountTemplate = renderKendoTemplate(
    <>
      {'# if (account) { #'}
      {'# if (account == window.location.pathname) { #'}
      <span>#= accountName #</span>
      {'# } else { #'}
      <u>
        <a href="#= account #">#= accountName #</a>
      </u>
      {'# } #'}
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const frequenciesTemplate = renderKendoTemplate(
    <>
      {'# if (frequencies) { #'}
      <span>#= frequencies #</span>
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const buildingTemplate = renderKendoTemplate(
    <>
      {'# if (building) { #'}
      {'# if (building == window.location.pathname) { #'}
      <span>#= buildingName #</span>
      {'# } else { #'}
      <u>
        <a href="#= building #">#= buildingName #</a>
      </u>
      {'# } #'}
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const actionsTemplate = renderKendoTemplate(
    <div>
      <button type="button" className="qmb-control--icon--row-actions --react">
        <i className="fa-regular fa-ellipsis" />
      </button>
      <div id="inspection_series_popup_#= id #" className="qmb-popup--action-list rerender-inspection-series-popup">
        <ul role="presentation">
          {'# if (showLink) { #'}
          <li>
            <a
              href="#= showLink #"
              title={translate('table.buttons.view_detail', { namespace })}
              className="qmb-control"
              target="blank"
              rel="noopener noreferrer">
              <i className="fa-light fa-eye" /> {translate('table.buttons.view_detail', { namespace })}
            </a>
          </li>
          {'# } #'}

          {'# if (!isCancelled) { #'}
          <li>
            <a
              href="#= editStatusLink #"
              title={translate('table.buttons.cancel', { namespace })}
              className="qmb-control"
              target="blank"
              rel="noopener noreferrer"
              data-remote="true">
              <i className="fa-light fa-ban" /> {translate('table.buttons.cancel', { namespace })}
            </a>
          </li>

          {'# if (buildingId) { #'}
          <li>
            <button
              type="button"
              title={translate('table.buttons.edit_expiration', { namespace })}
              className="qmb-control js-edit-expiration"
              data-series-id="#= id #"
              data-building-id="#= buildingId #"
              data-end-date="#= endDate #">
              <i className="fa-light fa-calendar-pen" />
              {translate('table.buttons.edit_expiration', { namespace })}
            </button>
          </li>
          {'# } #'}
          {'# } #'}
        </ul>
      </div>
    </div>
  );

  const saveCacheToLocalStorage = () => {
    if (!isMountedForCallbacks()) return;

    const grid = $(tableRef.current).data('kendoGrid');
    const cacheData = {};

    cacheData.hiddenColumns = grid.columns.filter((column) => column.hidden).map((column) => column.field);
    cacheData.stateFilters = filtersRef.current;
    // eslint-disable-next-line no-underscore-dangle
    cacheData.kendoSortSettings = grid.dataSource._sort;
    cacheData.page = grid.dataSource.page();

    const cacheItem = {
      data: cacheData
    };

    localStorage.setItem(kendoCacheKey, kendo.stringify(cacheItem));
  };

  const setupTableControls = () => {
    $(tableRef.current).on('change', '[data-js-bulk-all-checkbox]', onSelectAllToggle);
    $(tableRef.current).on('click', '[data-js-bulk-single-checkbox]', onCheckboxToggle);
  };

  const onCheckboxToggle = (e) => {
    const $controls = $('.js-bulk-action', containerRef.current);

    const checkbox = e.currentTarget;
    const inspectionSeriesId = checkbox.getAttribute('data-inspection-series-id');

    const $anyCheckedCheckbox = $('[data-js-bulk-single-checkbox]:checked', tableRef.current);
    $controls.attr('disabled', $anyCheckedCheckbox.length === 0);

    if (checkbox.checked) {
      dispatch({ type: actionTypes.INSPECTION_SERIES_SELECTED, id: inspectionSeriesId });
    } else {
      $('[data-js-bulk-all-checkbox]', tableRef.current).prop('checked', false);
      dispatch({ type: actionTypes.INSPECTION_SERIES_DESELECTED, id: inspectionSeriesId });
    }
  };

  const onSelectAllToggle = (e) => {
    const $checkbox = $(e.currentTarget);

    if (e.currentTarget.checked) {
      $('[data-js-bulk-single-checkbox]', tableRef.current).prop('checked', true);
      dispatch({ type: actionTypes.INSPECTION_SERIESES_DESELECTED });

      $('[data-js-bulk-single-checkbox]', tableRef.current).each((_index, row) => {
        dispatch({ type: actionTypes.INSPECTION_SERIES_SELECTED, id: row.getAttribute('data-inspection-series-id') });
      });
    } else {
      $('[data-js-bulk-single-checkbox]', tableRef.current).prop('checked', false);
      dispatch({ type: actionTypes.INSPECTION_SERIESES_DESELECTED });
    }

    const $controls = $('.js-bulk-action', containerRef.current);
    $controls.attr('disabled', !$checkbox.is(':checked'));
  };

  const loadCacheFromLocalStorage = () => {
    if (!localStorage.getItem(kendoCacheKey)) return;

    try {
      const cacheItem = JSON.parse(localStorage.getItem(kendoCacheKey));

      const cacheData = cacheItem.data;
      const grid = $(tableRef.current).data('kendoGrid');

      if (cacheData.hiddenColumns) {
        cacheData.hiddenColumns.forEach((fieldName) => grid.hideColumn(fieldName));

        grid.columns
          .map((column) => column.field)
          .filter((column) => !cacheData.hiddenColumns.includes(column))
          .forEach((fieldName) => grid.showColumn(fieldName));
      } else {
        grid.columns.map((column) => column.field).forEach((fieldName) => grid.showColumn(fieldName));
      }

      // eslint-disable-next-line no-underscore-dangle
      if (cacheData.kendoSortSettings) grid.dataSource._sort = cacheData.kendoSortSettings;
      if (cacheData.stateFilters) {
        dispatch({ type: actionTypes.LOAD_FILTERS_FROM_CACHE, filters: cacheData.stateFilters });
      }
      if (cacheData.page) {
        cachedPage = cacheData.page;
      }
    } catch {
      console.log(`Issue with Kendo grid cache: ${kendoCacheKey}`);
      localStorage.removeItem(kendoCacheKey);
    }
  };

  const reloadTable = () => {
    const filtersData = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(state.filters)) {
      if (!value) continue;
      // Date filters require a bit of different checks around them in different places.
      // If that part would be missed, it won't be critical, such as Back-End is prepared for empty values.
      //
      // This part is only about highlighting the grid header with red background
      // when inspectionDate field was used in filters.
      if (isDateRangeStateField(state.filters[key]) && !value.startDate && !value.endDate) continue;

      filtersData.push({ field: key, operator: 'eq', value });
    }
    $(tableRef.current).data('kendoGrid').dataSource.filter(filtersData);
  };

  const reloadTableControls = () => {
    const $controls = $('.js-bulk-action', containerRef.current);
    $controls.attr('disabled', true);

    const $selectAll = $('[data-js-bulk-all-checkbox], [data-js-bulk-single-checkbox]', tableRef.current);
    $selectAll.prop('checked', false);
  };

  const onBulkExtend = (e) => {
    e.preventDefault();
    // eslint-disable-next-line no-alert
    if (!state.inspection_series.length) return alert(translate('table.select_series', { namespace }));

    $('#inspection_series_ids').val(state.inspection_series.join());

    $('#inspection_series_extend_by_years').on('change', (event) => {
      const extendExpirationDateByYears = parseInt(event.target.value, 10) || 0;

      if (extendExpirationDateByYears < 1 || extendExpirationDateByYears > 10) {
        $('#validation-errors').text('Value must be >= 1 and <= 10');
      } else {
        $('#validation-errors').text('');
      }
    });

    $('#bulk-extend-series-submit').on('click', (event) => {
      if ($('#validation-errors').text().length) {
        event.preventDefault();
      }
    });

    $('#bulk-extend-series-modal').modal({ keyboard: false }).show();
  };

  const onChangeDateFilter = (fieldName) => {
    return (dateFieldName, value) => {
      dispatch({
        type: actionTypes.DATE_RANGE_FILTER_CHANGED,
        field: fieldName,
        dateField: dateFieldName,
        value
      });
      dispatch({ type: actionTypes.TABLE_RELOADED });
    };
  };

  const onChangeCommonFilter = (fieldName) => {
    return (e) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, field: fieldName, value: e.target.value });
      dispatch({ type: actionTypes.TABLE_RELOADED });
    };
  };

  const handleMultiSelectFilterChange = (fieldName) => {
    return (value) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, field: fieldName, value });
      dispatch({ type: actionTypes.TABLE_RELOADED });
    };
  };

  const onResetFilters = () => {
    dispatch({ type: actionTypes.FILTERS_RESET });
    dispatch({ type: actionTypes.TABLE_RELOADED });
  };

  // For more complicated scenarios, when you have issues with cached Kendo Grid,
  // to clear all Kendo Grid caches, use the next snippet:
  //
  // Object
  //   .keys(localStorage)
  //   .filter(key => key.startsWith('kendo/'))
  //   .forEach(key => {
  //     console.log("Removing item: ", key);
  //     localStorage.removeItem(key)
  //   });
  //
  const onResetCache = () => {
    if (!confirm(I18n.t('generic.are_you_sure'))) return;

    localStorage.removeItem(kendoCacheKey);
    window.location.reload();
  };

  // ===================== HTML Part ================================
  return (
    <div ref={containerRef}>
      {!hasDisableAllFilters && (
        <article id="filters" className="qmb-filters">
          <span className="filters__label">{translate('filters.title', { namespace })}</span>

          <fieldset className="filters__group">
            {hasShowAccountFilter && !!accountsForSelect.length && (
              <div className="filters__option --active">
                <GenericMultiSelect
                  name="account"
                  value={state.filters.account || []}
                  dataUrl="/lookup?type=account"
                  valueMapUrl="/lookup/map?type=account"
                  elements={accountsForSelect}
                  label={translate('filters.account', { namespace })}
                  placeholder={`Search ${translate('plural_account', { namespace: 'filters' })}`}
                  onChange={handleMultiSelectFilterChange('account')}
                />
              </div>
            )}

            {!hasGlobalTerritoriesApplied() && !!territoriesForSelect.length && (
              <div className="filters__option --active">
                <GenericMultiSelect
                  name="territory"
                  value={state.filters.territory || []}
                  dataUrl="/lookup?type=territory&with_unassigned=true"
                  valueMapUrl="/lookup/map?type=territory&with_unassigned=true"
                  elements={territoriesForSelect}
                  label={translate('territory', { namespace: 'filters' })}
                  placeholder={`Search ${translate('plural_territory', { namespace: 'filters' })}`}
                  onChange={handleMultiSelectFilterChange('territory')}
                />
              </div>
            )}

            {hasShowBuildingFilter && !!buildingsForSelect.length && (
              <div className="filters__option --active">
                <GenericMultiSelect
                  name="building"
                  value={state.filters.building || []}
                  dataUrl="/lookup/buildings?type=building"
                  valueMapUrl="/lookup/map?type=building"
                  elements={buildingsForSelect}
                  label={translate('filters.building', { namespace })}
                  placeholder={`Search ${translate('plural_building', { namespace: 'filters' })}`}
                  onChange={handleMultiSelectFilterChange('building')}
                  template="
                  <div>#: label #</div>
                  <sub>#: address #</sub>
                  "
                />
              </div>
            )}

            {hasShowExpirationDateFilter && (
              <DateRangePicker
                name="expiration_date"
                onAnyChange={onChangeDateFilter('expirationDate')}
                values={{
                  rangeType: state.filters.expirationDate.rangeType,
                  startDate: state.filters.expirationDate.startDate,
                  endDate: state.filters.expirationDate.endDate
                }}
                label={translate('filters.expiration_date', { namespace })}
                reactOptions={{ id: 'expiration-date' }}
              />
            )}

            {hasShowStatusFilter && (
              <div className="filters__option--native --active">
                <BooleanSelect
                  name="status"
                  value={state.filters.status}
                  label={translate('filters.status', { namespace })}
                  placeholder={I18n.t('generic.all')}
                  selectOptions={{
                    id: 'status',
                    onChange: onChangeCommonFilter('status')
                  }}
                />
              </div>
            )}

            {hasShowAutoExtendFilter && (
              <div className="filters__option--native --active">
                <BooleanSelect
                  name="auto_extend"
                  value={state.filters.autoExtend}
                  label={translate('filters.auto_extend', { namespace })}
                  placeholder={I18n.t('generic.all')}
                  selectOptions={{
                    id: 'auto-extend',
                    onChange: onChangeCommonFilter('autoExtend')
                  }}
                />
              </div>
            )}

            {hasShowExpiredSeriesFilter && (
              <div className="filters__option--native --active">
                <BooleanSelect
                  name="expired_series"
                  value={state.filters.expiredSeries}
                  label={translate('filters.expired_series', { namespace })}
                  selectOptions={{
                    id: 'expired_series',
                    onChange: onChangeCommonFilter('expiredSeries')
                  }}
                />
              </div>
            )}
          </fieldset>
          <fieldset className="filters__actions">
            <div role="button" tabIndex="-3" className="qmb-control--sm--reset" onClick={onResetFilters}>
              <i className="fa-light fa-arrow-rotate-left" />
              {I18n.t('generic.reset')}
            </div>
            {!isProduction && (
              <div role="button" tabIndex="-4" className="qmb-control--sm--reset" onClick={onResetCache}>
                {I18n.t('generic.reset_cache')}
              </div>
            )}
          </fieldset>
        </article>
      )}

      {!hasDisableAllFilters && !hasDisableAllBulkActions && <hr className="workspace__split" />}

      {!hasDisableAllBulkActions && (
        <div className="qmb-content-actions">
          <div className="content-actions__group">
            <div role="button" tabIndex="0" className="qmb-control js-bulk-action" onClick={onBulkExtend} disabled>
              <i className="fa-light fa-rectangle-history-circle-plus" />
              {translate('table.buttons.bulk_extend', { namespace })}
            </div>
          </div>
        </div>
      )}

      <CustomScrollbar>
        <article className="--scroll-x --disable-back-gesture">
          <div ref={tableRef} id="inspection-series-grid" data-src-url={inspectionSeriesUrl} className="qmb-table" />
        </article>
      </CustomScrollbar>

      {editingSeries && (
        <EditEndDateModal
          key={editingSeries.id}
          inspectionSeriesId={Number(editingSeries.id)}
          buildingId={Number(editingSeries.buildingId)}
          currentEndDate={String(editingSeries.endDate)}
          minEndDate={minEndDate}
          maxEndDate={maxEndDate}
          autoOpen
          renderTrigger={false}
          onClose={() => setEditingSeries(null)}
          onSaved={(message) => {
            setEditingSeries(null);
            showSuccessToast(message);
            reloadTable();
          }}
        />
      )}
    </div>
  );
}

LegacyInspectionSeriesIndex.propTypes = {
  inspectionSeriesUrl: PropTypes.string.isRequired,
  minEndDate: PropTypes.string,
  maxEndDate: PropTypes.string,
  defaultHiddenColumns: PropTypes.arrayOf(PropTypes.string),
  nonHidableColumns: PropTypes.arrayOf(PropTypes.string),
  namespace: PropTypes.string,
  buildingsForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      data: PropTypes.object
    })
  ),
  accountsForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      data: PropTypes.object
    })
  ),
  territoriesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      data: PropTypes.object
    })
  ),
  cacheKey: PropTypes.string,
  hasShowExpiredSeriesFilter: PropTypes.bool,
  hasShowExpirationDateFilter: PropTypes.bool,
  hasShowBuildingFilter: PropTypes.bool,
  hasShowAccountFilter: PropTypes.bool,
  hasShowStatusFilter: PropTypes.bool,
  hasShowAutoExtendFilter: PropTypes.bool,
  hasDisableAllFilters: PropTypes.bool,
  hasDisableAllBulkActions: PropTypes.bool,
  pageSize: PropTypes.number
};

LegacyInspectionSeriesIndex.defaultProps = {
  namespace: DEFAULT_NAMESPACE,
  buildingsForSelect: [],
  accountsForSelect: [],
  cacheKey: DEFAULT_CACHE_KEY,
  defaultHiddenColumns: [],
  nonHidableColumns: DEFAULT_NON_HIDABLE_COLUMNS,
  hasShowExpiredSeriesFilter: true,
  hasShowExpirationDateFilter: true,
  hasShowBuildingFilter: true,
  hasShowAccountFilter: true,
  hasShowStatusFilter: true,
  hasShowAutoExtendFilter: true,
  hasDisableAllFilters: false,
  hasDisableAllBulkActions: false,
  pageSize: DEFAULT_PAGE_SIZE
};

export default LegacyInspectionSeriesIndex;

/* eslint-enable react/no-unescaped-entities, import/order, jsx-a11y/control-has-associated-label, react/require-default-props, prettier/prettier */
